import { privateClient } from "@/api/privateClient";
import { INSTITUTION_ADMIN_ENDPOINTS } from "../../institutionAdmin.endpoints";
import type { StudentSort, StudentListRow, StudentDetail, Enrollment, Pagination, PromotePayload, ChangeSectionPayload, StudentDocument, EditStudentPayload } from "../types";

const STUDENTS = INSTITUTION_ADMIN_ENDPOINTS.STUDENTS;

export async function fetchStudents(
  institutionId: number,
  params?: { page?: number; limit?: number; academicYearId?: number; academicGroupId?: number; status?: "ACTIVE" | "INACTIVE"; search?: string; feeStatus?: "ANY_DUE" | "OVERDUE" | "PAID" | "NO_INVOICE"; minDue?: number; maxDue?: number; examId?: number; examResult?: "PASS" | "FAIL" | "PENDING"; categoryId?: number; gender?: "MALE" | "FEMALE" | "OTHER"; siblings?: "YES" | "NO"; admittedFrom?: string; admittedTo?: string; sort?: StudentSort }
): Promise<{ students: StudentListRow[]; pagination: Pagination; statusCounts?: { active: number; inactive: number } }> {
  const { data } = await privateClient.get(STUDENTS.LIST(institutionId), { params });
  return data;
}

/** Every student matching the filters, fetched page by page (100 per request) — for pickers that need the whole roster, not one capped page. */
export async function fetchAllStudents(
  institutionId: number,
  params?: { academicYearId?: number; academicGroupId?: number; status?: "ACTIVE" | "INACTIVE"; search?: string }
): Promise<StudentListRow[]> {
  const all: StudentListRow[] = [];
  let page = 1;
  let totalPages = 1;
  do {
    const res = await fetchStudents(institutionId, { ...params, page, limit: 100 });
    all.push(...res.students);
    totalPages = res.pagination.totalPages;
    page += 1;
  } while (page <= totalPages);
  return all;
}

export async function fetchStudent(studentId: number): Promise<StudentDetail> {
  const { data } = await privateClient.get(STUDENTS.DETAIL(studentId));
  return data.student;
}

/** Admin-driven reset — new one-time temp password, shown once; revokes the student's active sessions. */
export async function resetStudentPassword(userId: number): Promise<{ tempPassword: string }> {
  const { data } = await privateClient.post(STUDENTS.RESET_PASSWORD(userId));
  return data;
}

/** Admin-driven login-email change — independent of the student's guardian_email field. Revokes the student's active sessions. */
export async function changeStudentEmail(userId: number, email: string): Promise<{ user: { email: string } }> {
  const { data } = await privateClient.post(STUDENTS.CHANGE_EMAIL(userId), { email });
  return data;
}

export async function fetchEnrollments(studentId: number): Promise<Enrollment[]> {
  const { data } = await privateClient.get(STUDENTS.ENROLLMENTS(studentId));
  return data.enrollments;
}

export async function updateStudent(studentId: number, payload: Partial<EditStudentPayload>): Promise<StudentDetail> {
  const { data } = await privateClient.patch(STUDENTS.DETAIL(studentId), payload);
  return data.student;
}

/** Result of an admission/promotion's automatic fee assignment (best effort — see backend invoiceGenerationService). */
export interface FeeAssignmentResult {
  assigned: boolean;
  alreadyExisted?: boolean;
  reason?: string;
}

export async function promoteStudent(studentId: number, payload: PromotePayload): Promise<{ enrollment: Enrollment; feeAssignment?: FeeAssignmentResult }> {
  const { data } = await privateClient.post(STUDENTS.PROMOTE(studentId), payload);
  return { enrollment: data.enrollment, feeAssignment: data.feeAssignment };
}

export async function changeStudentSection(studentId: number, payload: ChangeSectionPayload): Promise<Enrollment> {
  const { data } = await privateClient.post(STUDENTS.CHANGE_SECTION(studentId), payload);
  return data.enrollment;
}

/** Corrects when the student joined their current class (null = the whole year). Decides which days their attendance is measured against. */
export async function updateStudentJoinDate(studentId: number, enrolledOn: string | null): Promise<Enrollment> {
  const { data } = await privateClient.patch(STUDENTS.JOIN_DATE(studentId), { enrolledOn });
  return data.enrollment;
}

export async function withdrawStudent(studentId: number): Promise<void> {
  await privateClient.post(STUDENTS.WITHDRAW(studentId));
}

export async function fetchStudentDocuments(studentId: number): Promise<StudentDocument[]> {
  const { data } = await privateClient.get(STUDENTS.DOCUMENTS.LIST(studentId));
  return data.documents;
}

export async function uploadStudentDocument(studentId: number, documentType: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append("document", file);
  await privateClient.post(STUDENTS.DOCUMENTS.UPLOAD(studentId, documentType), formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function deleteStudentDocument(studentId: number, documentType: string): Promise<void> {
  await privateClient.delete(STUDENTS.DOCUMENTS.DELETE(studentId, documentType));
}

/** Live search for linking a sibling (admission form). Space-separated terms must all match, e.g. "john 10A". */
export async function searchSiblings(
  institutionId: number,
  q: string,
  params?: { excludeStudentId?: number; limit?: number },
): Promise<StudentListRow[]> {
  const { data } = await privateClient.get(`${STUDENTS.LIST(institutionId)}/sibling-search`, {
    params: { q, limit: 20, ...params },
  });
  return data.students ?? [];
}
/** Sets the roll number by hand for the student's current class; an empty value returns it to auto-numbering. */
export async function updateStudentRollNumber(studentId: number, rollNumber: string): Promise<Enrollment> {
  const { data } = await privateClient.patch(STUDENTS.ROLL_NUMBER(studentId), { rollNumber });
  return data.enrollment;
}
